"use client";

/**
 * Número que muda sem piscar. Dois modos:
 *
 * - `rolar` (padrão, KPI e valor de destaque): só os dígitos que mudaram rolam,
 *   cada um na própria coluna, na mola `suave`. O olho lê a diferença, não um
 *   borrão de algarismos.
 * - `contar` (contador pequeno, total de lista): o valor passa pelos
 *   intermediários, sem re-render do React a cada quadro (motion value).
 *
 * Na chegada o número aparece pronto; só a MUDANÇA anima (com moderação: é
 * dado, não espetáculo). Reduzido: troca seca.
 */
import { m, useMotionValue, useSpring, useTransform } from "motion/react";
import { useEffect, useRef, type CSSProperties } from "react";
import { MOLA } from "./tokens";
import { useMovimento } from "./provider";

const formatoPadrao = new Intl.NumberFormat("pt-BR");
const DIGITOS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

export function NumeroAnimado({
  valor,
  formatar = (n) => formatoPadrao.format(n),
  modo = "rolar",
  className,
  style,
  contarNaChegada = false,
}: {
  valor: number;
  formatar?: (n: number) => string;
  modo?: "rolar" | "contar";
  className?: string;
  style?: CSSProperties;
  /** Conta a partir do zero na primeira pintura (só no modo `contar`). */
  contarNaChegada?: boolean;
}) {
  const mov = useMovimento();
  const texto = formatar(valor);

  if (mov.reduzido || mov.escala === 0) {
    return (
      <span className={className} style={{ fontVariantNumeric: "tabular-nums", ...style }}>
        {texto}
      </span>
    );
  }

  if (modo === "contar") {
    return (
      <Contar valor={valor} formatar={formatar} className={className} style={style} contarNaChegada={contarNaChegada} />
    );
  }

  // Alinha pela DIREITA: a unidade continua sendo a unidade quando o número cresce.
  const caracteres = texto.split("");
  return (
    <span className={className ? `mov-num ${className}` : "mov-num"} style={{ fontVariantNumeric: "tabular-nums", display: "inline-flex", ...style }}>
      <span className="sr-only">{texto}</span>
      <span aria-hidden style={{ display: "inline-flex", whiteSpace: "pre" }}>
        {caracteres.map((c, i) => {
          const posicao = caracteres.length - i;
          return /\d/.test(c) ? (
            <Coluna key={`d${posicao}`} digito={Number(c)} />
          ) : (
            <span key={`s${posicao}-${c}`}>{c}</span>
          );
        })}
      </span>
    </span>
  );
}

function Coluna({ digito }: { digito: number }) {
  const mov = useMovimento();
  return (
    <span className="mov-dig" style={{ display: "inline-block", height: "1lh", overflow: "hidden", verticalAlign: "top" }}>
      <m.span
        initial={false}
        animate={{ y: `${-digito * 10}%` }}
        transition={mov.mola("suave")}
        style={{ display: "flex", flexDirection: "column" }}
      >
        {DIGITOS.map((d) => (
          <span key={d} style={{ height: "1lh" }}>
            {d}
          </span>
        ))}
      </m.span>
    </span>
  );
}

function Contar({
  valor,
  formatar,
  className,
  style,
  contarNaChegada,
}: {
  valor: number;
  formatar: (n: number) => string;
  className?: string;
  style?: CSSProperties;
  contarNaChegada: boolean;
}) {
  const mov = useMovimento();
  const bruto = useMotionValue(contarNaChegada ? 0 : valor);
  const suave = useSpring(bruto, {
    visualDuration: MOLA.suave.visualDuration * mov.escala,
    bounce: 0,
  });
  const texto = useTransform(suave, (v) => formatar(v));
  const primeira = useRef(true);
  useEffect(() => {
    if (primeira.current && !contarNaChegada) {
      primeira.current = false;
      return;
    }
    primeira.current = false;
    bruto.set(valor);
  }, [valor, bruto, contarNaChegada]);
  return (
    <m.span className={className} style={{ fontVariantNumeric: "tabular-nums", ...style }}>
      {texto}
    </m.span>
  );
}
